import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, waLink } from "@/lib/api";
import { useSiteInfo } from "@/lib/useSiteInfo";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

const OWNER_TYPES = ["NRI", "Senior Citizen", "Local Owner", "Out-of-Station Owner"];
const PROPERTY_TYPES = ["Vacant Plot", "Apartment", "Independent House", "Villa", "Commercial"];
const SERVICES = ["Plot Monitoring", "Rental Management", "Vacant Home Watch", "Senior Citizen Support", "NRI Property Management", "Not Sure"];

export default function Contact() {
  const info = useSiteInfo();
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "",
    owner_type: "NRI", property_type: "Apartment", location: "",
    service_needed: "Not Sure", message: "",
  });
  const [busy, setBusy] = useState(false);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.location) {
      toast.error("Please fill name, WhatsApp and property location.");
      return;
    }
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      await api.post("/contact", payload);
      toast.success("Thanks! Our Chennai team will reach out within 24 hours.");
      setForm({ ...form, name: "", whatsapp: "", email: "", location: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit. Please WhatsApp us.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-brand-navy text-white">
        <div className="container-x py-20 md:py-28">
          <div className="eyebrow !text-brand-cyan">Contact</div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Tell us about your property. We will recommend the right care plan.
          </h1>
        </div>
      </section>

      <section className="container-x -mt-12 mb-16 grid lg:grid-cols-12 gap-8">
        <form data-testid="contact-form" onSubmit={submit} className="lg:col-span-8 bg-white border border-brand-border p-7 md:p-10">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name" name="name" value={form.name} onChange={change} testid="contact-name" />
            <Field label="WhatsApp Number" name="whatsapp" value={form.whatsapp} onChange={change} testid="contact-whatsapp" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={change} testid="contact-email" required={false} />
            <Field label="Property Location" name="location" value={form.location} onChange={change} testid="contact-location" />
            <Select label="Owner Type" name="owner_type" value={form.owner_type} onChange={change} options={OWNER_TYPES} testid="contact-owner-type" />
            <Select label="Property Type" name="property_type" value={form.property_type} onChange={change} options={PROPERTY_TYPES} testid="contact-property-type" />
            <Select label="Service Needed" name="service_needed" value={form.service_needed} onChange={change} options={SERVICES} testid="contact-service" className="sm:col-span-2" />
            <label className="block sm:col-span-2">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Message</span>
              <textarea data-testid="contact-message" name="message" rows={4} value={form.message} onChange={change}
                className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan"
                placeholder="Tell us about your property and current concern."/>
            </label>
          </div>
          <button data-testid="contact-submit" disabled={busy} className="btn-cyan mt-6 w-full md:w-auto disabled:opacity-60">
            {busy ? "Sending…" : "Get My Property Care Plan"}
          </button>
          <p className="text-[11px] mt-3 text-brand-slate/60">
            Your details are kept confidential. We use your property information only to understand the service scope and recommend the right plan.
          </p>
        </form>

        <aside className="lg:col-span-4 bg-brand-navy text-white p-7 md:p-10">
          <div className="eyebrow !text-brand-cyan">Chennai Office</div>
          <h3 className="mt-3 font-display text-2xl font-bold">Talk to a property manager.</h3>
          <ul className="mt-6 space-y-3 text-sm text-white/85">
            <li className="flex items-start gap-2"><MapPin size={16} className="text-brand-cyan mt-0.5 flex-shrink-0" /> <span>{info.address}</span></li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-brand-cyan" /> <a href={`tel:${info.phone.replace(/\s+/g, "")}`} className="hover:text-brand-cyan">{info.phone}</a></li>
            <li className="flex items-center gap-2"><Mail size={16} className="text-brand-cyan" /> <a href={`mailto:${info.email}`} className="hover:text-brand-cyan">{info.email}</a></li>
          </ul>
          <a href={waLink("Hi Hi-Tech Realty, I want to speak to a property manager.")}
             target="_blank" rel="noreferrer"
             className="btn-whatsapp w-full mt-8" data-testid="contact-whatsapp-cta">
            <MessageCircle size={14} /> WhatsApp Chennai Team
          </a>
          <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/70 leading-relaxed">
            "Your property deserves more than occasional attention." We respond within 24 hours, Monday to Saturday.
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, testid, type="text", required=true }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <input data-testid={testid} required={required} type={type} name={name} value={value} onChange={onChange}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" />
    </label>
  );
}
function Select({ label, name, value, onChange, options, testid, className="" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <select data-testid={testid} name={name} value={value} onChange={onChange}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
